import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';
import type { FormEnvelope } from '@/forms/types';

export type JobForm = components['schemas']['JobFormResponse'];
export type FormTemplateSummary = components['schemas']['FormTemplateSummaryResponse'];
export type FormSubmission = components['schemas']['FormSubmissionResponse'];

export function jobFormsQueryKey(jobId: string) {
  return ['jobs', jobId, 'forms'] as const;
}

/**
 * Which forms apply to this job, and what has been filled in so far (M1-20).
 *
 * One request, already resolved server-side: the assignment (per job or per job
 * type), the current published version of each template, and the latest
 * submission. The page never joins those itself — the completion guard in the
 * API reads the same resolver, and a second implementation here is exactly how
 * the two would come to disagree about whether a job is ready.
 */
export function useJobForms(jobId: string | undefined) {
  return useQuery({
    queryKey: jobFormsQueryKey(jobId ?? ''),
    enabled: jobId !== undefined,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/jobs/{jobId}/forms', {
        params: { path: { jobId: jobId! } },
      });
      if (response.status === 403) {
        // The technician-not-on-this-job case. The page already renders its own
        // "not yours" state from the job query; this section just stays empty
        // rather than shouting a second error about the same fact.
        return [];
      }
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/jobs/{jobId}/forms failed: ${response.status}`);
      }
      return data;
    },
  });
}

/** A template's `{schema, uiSchema}` envelope — what the renderer draws from. */
export function useFormTemplate(templateId: string | undefined) {
  return useQuery({
    queryKey: ['form-templates', templateId],
    enabled: templateId !== undefined,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/form-templates/{id}', {
        params: { path: { id: templateId! } },
      });
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/form-templates/{id} failed: ${response.status}`);
      }
      return data;
    },
    // A published template version is immutable by construction (M1-16: one row
    // per version, never edited in place), so there is nothing to re-fetch.
    staleTime: Infinity,
  });
}

/** Published templates a job can have attached — the attach dialog's picker. */
export function useJobFormTemplates(enabled: boolean) {
  return useQuery({
    queryKey: ['form-templates', 'job'],
    enabled,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/form-templates', {
        params: { query: { entity: 'Job' } },
      });
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/form-templates failed: ${response.status}`);
      }
      return data;
    },
  });
}

export function useFormSubmission(submissionId: string | undefined) {
  return useQuery({
    queryKey: ['form-submissions', submissionId],
    enabled: submissionId !== undefined,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/form-submissions/{id}', {
        params: { path: { id: submissionId! } },
      });
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/form-submissions/{id} failed: ${response.status}`);
      }
      return data;
    },
  });
}

/** The API's own refusal text, kept rather than replaced with a generic message. */
export class FormSaveRefusedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FormSaveRefusedError';
  }
}

export interface SaveJobFormInput {
  formTemplateId: string;
  /** Set to update an existing draft; omitted to start a new submission. */
  submissionId?: string;
  values: Record<string, unknown>;
  submit: boolean;
}

/**
 * Save a draft or submit, whichever the caller asked for, through whichever of
 * M1-19's three write endpoints fits — create, update-a-draft, or both at once.
 *
 * The three are one mutation rather than three hooks because from the page's
 * point of view there is one action ("save what I have typed, and possibly
 * submit it") whose route into the API is an implementation detail of whether a
 * draft already exists.
 */
export function useSaveJobForm(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ formTemplateId, submissionId, values, submit }: SaveJobFormInput) => {
      if (submissionId) {
        const { data, error, response } = await apiClient.PUT('/api/v1/form-submissions/{id}', {
          params: { path: { id: submissionId } },
          body: { data: values, submit },
        });
        // 409 = already submitted, therefore immutable (api/CONVENTIONS.md,
        // M1-19). Read off `error`, never `data` — openapi-fetch populates
        // `data` for 2xx only (M1-05's note).
        if (response.status === 409) {
          throw new FormSaveRefusedError(typeof error === 'string' ? error : 'Already submitted.');
        }
        if (!response.ok || !data) {
          throw new Error(`PUT /api/v1/form-submissions/{id} failed: ${response.status}`);
        }
        return data;
      }

      const { data, response } = await apiClient.POST('/api/v1/form-submissions', {
        body: {
          formTemplateId,
          linkedToType: 'Job',
          linkedToId: jobId,
          data: values,
          submit,
        },
      });
      if (!response.ok || !data) {
        throw new Error(`POST /api/v1/form-submissions failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: (submission) => {
      void queryClient.invalidateQueries({ queryKey: jobFormsQueryKey(jobId) });
      void queryClient.invalidateQueries({ queryKey: ['form-submissions', submission.id] });
      // Submitting the last outstanding required form is what unblocks
      // completing the job, so the job's own allowed transitions are stale now.
      void queryClient.invalidateQueries({ queryKey: ['jobs', jobId] });
    },
  });
}

export function useAttachJobForm(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ formTemplateKey, required }: { formTemplateKey: string; required: boolean }) => {
      const { data, error, response } = await apiClient.POST('/api/v1/form-assignments', {
        body: { formTemplateKey, jobId, jobType: null, required },
      });
      if (response.status === 409) {
        throw new FormSaveRefusedError(typeof error === 'string' ? error : 'Already attached.');
      }
      if (!response.ok || !data) {
        throw new Error(`POST /api/v1/form-assignments failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobFormsQueryKey(jobId) });
      void queryClient.invalidateQueries({ queryKey: ['jobs', jobId] });
    },
  });
}

export function useDetachJobForm(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assignmentId: string) => {
      const { response } = await apiClient.DELETE('/api/v1/form-assignments/{id}', {
        params: { path: { id: assignmentId } },
      });
      if (!response.ok) {
        throw new Error(`DELETE /api/v1/form-assignments/{id} failed: ${response.status}`);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobFormsQueryKey(jobId) });
      void queryClient.invalidateQueries({ queryKey: ['jobs', jobId] });
    },
  });
}

/**
 * The API types `schema` as an opaque object (it is arbitrary JSON to OpenAPI),
 * so the cast happens here, once, rather than at every call site. The shape is
 * guaranteed by `IFormSchemaValidator`, which every write path runs before a
 * template is stored (docs/forms-schema.md).
 */
export function toEnvelope(schema: unknown): FormEnvelope {
  return schema as FormEnvelope;
}
